import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, RefreshCw, AlertTriangle, Home, Shield } from 'lucide-react';
import {
  acknowledgeOpenSession,
  clockIn,
  clockOut,
  getAttendanceToday,
  type AttendanceToday,
  type GeofenceStatus,
} from '../api/attendance';
import { getCurrentPosition } from '../lib/geolocation';
import { APP_LOCALE, formatHours, formatMinutes, formatTime } from '@/lib/format';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import LoadingState from './ui/LoadingState';
import { cn } from '@/lib/utils';

type GeoClockCardProps = {
  compact?: boolean;
  showHistoryLink?: boolean;
};

function formatWorkedShort(hours: number): string {
  const totalMinutes = Math.max(0, Math.round(hours * 60));
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

function minutesSinceClockIn(clockInTime: string): number {
  const isIso = clockInTime.includes('T');
  const clockInDate = isIso ? new Date(clockInTime) : new Date(`1970-01-01T${clockInTime}`);
  if (Number.isNaN(clockInDate.getTime())) return 0;
  if (isIso) {
    return Math.max(0, Math.floor((Date.now() - clockInDate.getTime()) / 60_000));
  }
  const now = new Date();
  const todayClockIn = new Date(now.getFullYear(), now.getMonth(), now.getDate(), clockInDate.getHours(), clockInDate.getMinutes());
  return Math.max(0, Math.floor((now.getTime() - todayClockIn.getTime()) / 60_000));
}

function currentTimeLabel(): string {
  return new Date().toLocaleTimeString(APP_LOCALE, { hour: '2-digit', minute: '2-digit' });
}

export default function GeoClockCard({ compact = false, showHistoryLink = true }: GeoClockCardProps) {
  const [today, setToday] = useState<AttendanceToday | null>(null);
  const [geofence, setGeofence] = useState<GeofenceStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [locating, setLocating] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const actionInFlight = useRef(false);
  const [confirmClockOut, setConfirmClockOut] = useState(false);
  const [error, setError] = useState('');
  const [openSessionDismissed, setOpenSessionDismissed] = useState(false);
  const [outstationPrompt, setOutstationPrompt] = useState<'in' | 'out' | null>(null);
  const [outstationReason, setOutstationReason] = useState('');
  const lastCoords = useRef<{ latitude: number; longitude: number } | undefined>(undefined);
  const [liveTime, setLiveTime] = useState(currentTimeLabel);

  const refresh = useCallback(async () => {
    setLocating(true);
    setError('');
    try {
      const coords = await getCurrentPosition();
      lastCoords.current = coords;
      const res = await getAttendanceToday(coords);
      if (res.data?.data) {
        setToday(res.data.data);
        if (res.data.data.geofence) setGeofence(res.data.data.geofence);
      } else if (res.error) setError(res.error);
    } catch (err) {
      const fallback = await getAttendanceToday();
      if (fallback.data?.data) {
        setToday(fallback.data.data);
        if (fallback.data.data.geofence) setGeofence(fallback.data.data.geofence);
      }
      setError(err instanceof Error ? err.message : 'Location unavailable');
    } finally {
      setLocating(false);
      setLoading(false);
    }
  }, []);

  const pollTodayStatus = useCallback(async () => {
    const res = await getAttendanceToday(lastCoords.current);
    if (res.data?.data) {
      setToday(res.data.data);
      if (res.data.data.geofence) setGeofence(res.data.data.geofence);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      void pollTodayStatus();
    }, 15_000);

    return () => window.clearInterval(interval);
  }, [pollTodayStatus]);

  useEffect(() => {
    let intervalId: number | undefined;
    const now = new Date();
    const msToNextMinute = 60_000 - (now.getSeconds() * 1000 + now.getMilliseconds());
    const timeoutId = window.setTimeout(() => {
      setLiveTime(currentTimeLabel());
      intervalId = window.setInterval(() => {
        setLiveTime(currentTimeLabel());
      }, 60_000);
    }, msToNextMinute);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') setLiveTime(currentTimeLabel());
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.clearTimeout(timeoutId);
      if (intervalId !== undefined) window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  useEffect(() => {
    setOpenSessionDismissed(Boolean(today?.openSession?.acknowledgedAt));
  }, [today?.openSession?.acknowledgedAt]);

  const runClock = async (
    action: 'in' | 'out',
    remote = false,
    outstation = false,
    reason?: string
  ) => {
    if (actionInFlight.current) return;
    actionInFlight.current = true;
    setActionLoading(true);
    setError('');
    try {
      let coords: { latitude: number; longitude: number } | undefined;
      try {
        coords = await getCurrentPosition();
        lastCoords.current = coords;
      } catch (gpsErr) {
        if (geofence?.enabled && !remote) {
          setError(gpsErr instanceof Error ? gpsErr.message : 'Location unavailable. Enable GPS to clock in.');
          return;
        }
      }
      const res =
        action === 'in'
          ? await clockIn(coords, undefined, remote, outstation, reason)
          : await clockOut(coords, undefined, outstation, reason);
      if (res.error) setError(res.error);
      else {
        if (action === 'out') setConfirmClockOut(false);
        setOutstationPrompt(null);
        setOutstationReason('');
        await refresh();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : `Clock ${action} failed`);
    } finally {
      setActionLoading(false);
      actionInFlight.current = false;
    }
  };



  if (loading) {
    return <LoadingState label="Loading attendance…" rows={compact ? 1 : 2} />;
  }

  const siteName = geofence?.matchedSite?.name ?? geofence?.office?.name ?? 'office';
  const geofenceOn = geofence?.enabled === true;
  const isClockedIn = today?.isClockedIn === true;
  const isOnLeave = today?.status === 'LEAVE';
  const isRemoteSession = today?.clockInMode === 'REMOTE';
  const canClockInToday = !isOnLeave && !today?.isClockedIn;
  const isOutstationIn = today?.clockInMode === 'OUTSTATION';
  const isOutstationOut = today?.clockOutMode === 'OUTSTATION';
  const locationBlocksClock = geofenceOn && !isRemoteSession && (
    (canClockInToday && geofence?.canClockIn === false) ||
    (isClockedIn && geofence?.canClockOut === false)
  );
  const canUseWfh = canClockInToday && geofence?.remoteAvailable === true && geofence?.canClockIn === false;
  const canUseOutstationIn = canClockInToday && geofenceOn && geofence?.canClockIn === false;
  const canUseOutstationOut = isClockedIn && !isRemoteSession && geofenceOn && geofence?.canClockOut === false;


  const statusLabel = isOnLeave
    ? 'On leave'
    : today?.isClockedOut
    ? 'Shift complete'
    : isClockedIn
      ? 'Currently clocked in'
      : 'Not clocked in';

  const openSession = today?.openSession;
  const dismissOpenSession = () => {
    if (!openSession) return;
    void (async () => {
      const res = await acknowledgeOpenSession();
      if (res.error) {
        setError(res.error);
        return;
      }
      setOpenSessionDismissed(true);
      await pollTodayStatus();
    })();
  };

  return (
    <div className="space-y-3">
      {openSession && !openSessionDismissed && (
        <div className="flex items-center gap-2 rounded-lg border border-warning/20 bg-warning/10 px-3 py-2.5">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-warning" />
          <p className="min-w-0 flex-1 text-sm text-warning">
            Missed clock-out on {openSession.date} at {openSession.clockInTime}
          </p>
          <button
            type="button"
            onClick={dismissOpenSession}
            className="shrink-0 text-xs font-medium text-warning underline-offset-2 hover:underline"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        <Badge variant={isOnLeave ? 'warning' : isClockedIn ? 'success' : today?.isClockedOut ? 'secondary' : 'secondary'} className="gap-1.5">
          <span
            className={cn(
              'h-1.5 w-1.5 rounded-full',
              isOnLeave ? 'bg-warning' : isClockedIn ? 'bg-success' : 'bg-muted-foreground/50'
            )}
          />
          {statusLabel}
        </Badge>
        {today?.clockInTime && (
          <span className="text-xs text-muted-foreground">
            In {formatTime(today.clockInTime)}
            {today.clockOutTime ? ` · Out ${formatTime(today.clockOutTime)}` : ''}
          </span>
        )}
      </div>

      <div className="rounded-2xl border border-border bg-muted/40 p-4">
        <div className="flex items-baseline gap-2">
          <span className="text-4xl font-semibold leading-none tracking-tight tabular-nums text-foreground whitespace-nowrap">
            {liveTime}
          </span>
          <span className="text-xs text-muted-foreground whitespace-nowrap">current time</span>
        </div>
        <div className="mt-4 grid grid-cols-3 rounded-xl bg-card">
          <div className="px-2 py-3 text-center">
            <p className="text-xs uppercase tracking-wide text-muted-foreground whitespace-nowrap">Clock in</p>
            <p className="mt-1 text-sm font-semibold tabular-nums text-foreground whitespace-nowrap">
              {today?.clockInTime ? formatTime(today.clockInTime) : '--:--'}
            </p>
          </div>
          <div className="border-x border-border px-2 py-3 text-center">
            <p className="text-xs uppercase tracking-wide text-muted-foreground whitespace-nowrap">Clock out</p>
            <p className="mt-1 text-sm font-semibold tabular-nums text-foreground whitespace-nowrap">
              {today?.clockOutTime ? formatTime(today.clockOutTime) : '--:--'}
            </p>
          </div>
          <div className="px-2 py-3 text-center">
            <p className="text-xs uppercase tracking-wide text-muted-foreground whitespace-nowrap">Worked</p>
            <p className="mt-1 text-sm font-semibold tabular-nums text-foreground whitespace-nowrap">
              {isClockedIn && today?.clockInTime
                ? formatWorkedShort(minutesSinceClockIn(today.clockInTime) / 60)
                : formatWorkedShort(today?.workHours ?? 0)}
            </p>
          </div>
        </div>
      </div>

      {(today?.isLate || today?.isEarlyDeparture || isOutstationIn || isOutstationOut || isRemoteSession || (today?.isClockedOut && today.workHours > 0)) && (
        <div className="flex flex-wrap gap-1.5">
          {today?.isLate && (
            <Badge variant="destructive">
              Late{today.lateMinutes ? ` ${formatMinutes(today.lateMinutes)}` : ''}
            </Badge>
          )}
          {isRemoteSession && (
            <Badge variant="info" className="gap-1">
              <Home className="h-3 w-3" />
              WFH
            </Badge>
          )}
          {isOutstationIn && (
            <Badge variant="plum">Outstation (in)</Badge>
          )}
          {isOutstationOut && (
            <Badge variant="plum">Outstation (out)</Badge>
          )}
          {today?.isEarlyDeparture && (
            <Badge variant="secondary">
              Undertime
              {today.earlyDepartureMinutes ? ` ${formatMinutes(today.earlyDepartureMinutes)}` : ''}
            </Badge>
          )}
          {today?.isClockedOut && today.workHours > 0 && (
            <Badge variant="success">{formatHours(today.workHours)} worked today</Badge>
          )}
        </div>
      )}

      {geofenceOn && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <MapPin className="h-3 w-3 text-primary" />
            {geofence.withinRange === true
              ? `At ${siteName}`
              : geofence.withinRange === false
                ? `${geofence.distanceMeters}m from ${siteName}`
                : 'Enable location to verify'}
          </span>
          <button
            type="button"
            onClick={() => refresh()}
            disabled={locating}
            className="flex items-center gap-1 text-primary"
          >
            <RefreshCw className={cn('h-3 w-3', locating && 'animate-spin')} />
            {locating ? 'Locating…' : 'Refresh'}
          </button>
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {isOnLeave && !today?.isClockedIn && !today?.isClockedOut && (
        <Button className="w-full" size="lg" disabled>
          You are on leave today
        </Button>
      )}

      {canClockInToday && (
        <Button
          className="w-full"
          size="lg"
          onClick={() => runClock('in')}
          disabled={actionLoading || locating || locationBlocksClock}
        >
          {actionLoading ? 'Clocking in…' : 'Clock in'}
        </Button>
      )}

      {canUseWfh && (
        <Button
          className="w-full"
          size="lg"
          variant="outline"
          onClick={() => runClock('in', true)}
          disabled={actionLoading || locating}
        >
          <Home className="mr-2 h-4 w-4" />
          {actionLoading ? 'Clocking in…' : `Work from home — ${geofence?.remoteSite?.name ?? 'Remote'}`}
        </Button>
      )}

      {canUseOutstationIn && outstationPrompt !== 'in' && (
        <Button
          className="w-full"
          size="lg"
          variant="outline"
          onClick={() => setOutstationPrompt('in')}
          disabled={actionLoading || locating}
        >
          <MapPin className="mr-2 h-4 w-4" />
          Outstation clock in
        </Button>
      )}

      {outstationPrompt === 'in' && (
        <div className="space-y-3 rounded-xl border border-border bg-muted/40 p-4">
          <div>
            <p className="font-medium text-foreground">Outstation clock in</p>
            <p className="mt-1 text-sm text-muted-foreground">
              You're outside {siteName}. Your exact location will be recorded. Tell HR why.
            </p>
          </div>
          <textarea
            className="w-full rounded-lg border border-border bg-background p-2 text-sm"
            rows={2}
            placeholder="e.g. Client site visit for Acme Sdn Bhd"
            value={outstationReason}
            onChange={(e) => setOutstationReason(e.target.value)}
          />
          <div className="flex gap-2">
            <Button
              className="flex-1"
              variant="outline"
              onClick={() => {
                setOutstationPrompt(null);
                setOutstationReason('');
              }}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              className="flex-1"
              onClick={() => runClock('in', false, true, outstationReason.trim())}
              disabled={actionLoading || locating || outstationReason.trim().length === 0}
            >
              {actionLoading ? 'Clocking in…' : 'Confirm outstation clock in'}
            </Button>
          </div>
        </div>
      )}

      {isClockedIn && !confirmClockOut && (
        <Button
          className="w-full"
          size="lg"
          variant="outline"
          onClick={() => setConfirmClockOut(true)}
          disabled={actionLoading || locating || locationBlocksClock}
        >
          Clock out
        </Button>
      )}

      {isClockedIn && confirmClockOut && (
        <div className="space-y-3 rounded-xl border border-border bg-muted/40 p-4">
          <div>
            <p className="font-medium text-foreground">Clock out now?</p>
            <p className="mt-1 text-sm text-muted-foreground">
              This will end your shift for today
              {today?.clockInTime ? ` (clocked in at ${formatTime(today.clockInTime)})` : ''}.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              className="flex-1"
              variant="outline"
              onClick={() => setConfirmClockOut(false)}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              className="flex-1"
              onClick={() => runClock('out')}
              disabled={actionLoading || locating || locationBlocksClock}
            >
              {actionLoading ? 'Clocking out…' : 'Confirm clock out'}
            </Button>
          </div>
        </div>
      )}

      {canUseOutstationOut && !confirmClockOut && outstationPrompt !== 'out' && (
        <Button
          className="w-full"
          size="lg"
          variant="outline"
          onClick={() => setOutstationPrompt('out')}
          disabled={actionLoading || locating}
        >
          <MapPin className="mr-2 h-4 w-4" />
          Outstation clock out
        </Button>
      )}

      {outstationPrompt === 'out' && (
        <div className="space-y-3 rounded-xl border border-border bg-muted/40 p-4">
          <div>
            <p className="font-medium text-foreground">Outstation clock out</p>
            <p className="mt-1 text-sm text-muted-foreground">
              You're outside {siteName}. Your exact location will be recorded. Tell HR why.
            </p>
          </div>
          <textarea
            className="w-full rounded-lg border border-border bg-background p-2 text-sm"
            rows={2}
            placeholder="e.g. Delivering equipment to client"
            value={outstationReason}
            onChange={(e) => setOutstationReason(e.target.value)}
          />
          <div className="flex gap-2">
            <Button
              className="flex-1"
              variant="outline"
              onClick={() => {
                setOutstationPrompt(null);
                setOutstationReason('');
              }}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              className="flex-1"
              onClick={() => runClock('out', false, true, outstationReason.trim())}
              disabled={actionLoading || locating || outstationReason.trim().length === 0}
            >
              {actionLoading ? 'Clocking out…' : 'Confirm outstation clock out'}
            </Button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 pt-1 text-xs text-muted-foreground">
        <Shield className="h-3.5 w-3.5 shrink-0" />
        Time and location are recorded when you tap.
      </div>

      {!compact && showHistoryLink && (
        <Button variant="link" asChild className="w-full">
          <Link to="/attendance">View attendance history →</Link>
        </Button>
      )}
    </div>
  );
}
